import { fetchItems, fetchBrandBackgrounds, fetchShowcaseHiddenBrands } from "./inventoryWarmup.js";

// How many product shots each brand carries for hover previews. More than one
// so repeated passes over a row are not identical, but few enough that the
// browser is not asked to hold a whole catalogue of images in memory.
const SHOTS_PER_BRAND = 4;

const coverOf = (item) => {
  const photos = Array.isArray(item?.photos_json) ? item.photos_json : [];
  return photos[item?.cover_index || 0]?.url || photos[0]?.url || null;
};

// The inventory, tried a second time after a short wait when the first try
// fails: a cold load can meet a slow or briefly failing API, and one failure
// used to leave SHOWCASE's list empty.
async function fetchItemsRetrying() {
  try {
    return await fetchItems();
  } catch {
    await new Promise((resolve) => setTimeout(resolve, 700));
    return fetchItems();
  }
}

// Each tile backdrop loaded once per page load: the promise is kept, so every
// caller waits on the same request, and resolves (loaded or not) when the
// browser is done with it; the browser then serves the image from its cache.
const _imageLoads = new Map();
export function preloadImage(url) {
  if (!url) return Promise.resolve();
  let p = _imageLoads.get(url);
  if (!p) {
    p = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
    });
    _imageLoads.set(url, p);
  }
  return p;
}

// Resolves when every backdrop in the list has loaded, or after timeoutMs,
// whichever comes first.
export function waitForBrandImages(list, timeoutMs) {
  const loads = (list || []).map((b) => b.url).filter(Boolean).map(preloadImage);
  return Promise.race([
    Promise.all(loads),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

// Warms SHOWCASE before it is pressed: the list and its tiles' backdrops.
// Called when the pointer or focus reaches the header's SHOWCASE, so a cold
// first open finds them in hand; not on every page load, since the backdrops
// are large and most visits never open the list.
export function warmShowcase() {
  return fetchBrandList().then((list) => {
    list.forEach((b) => { preloadImage(b.url); });
    return list;
  });
}

// The last list fetchBrandList produced, if any: SHOWCASE shows it at once
// when it opens, and refreshes from fetchBrandList behind it.
let _lastList = null;
export function peekBrandList() {
  return _lastList;
}

// The one definition of "which brands exist, in what order, with what
// imagery and how many pieces".
//
// Three surfaces show this list — the landing page's diagonal slices, the
// /brands wall and the /brand index — and all of them have to agree with the
// carousel, or a brand can be offered that has no row to land on. The rules:
//   · a brand counts only if something of theirs is live (mirrors
//     BrandCarousel's brandRows grouping)
//   · a brand the owner hid from the Showcase (site manager's per-brand
//     switch) is not in it, for the same reason: the carousel has no row for it
//   · alphabetical, so positions correspond between surfaces
//   · `url` is the brand's BACKGROUND, null for video: only a still can be
//     painted as a CSS background, and callers dark-fill that slot instead
//   · `shots` are PRODUCT covers, for previews; `count` is how many pieces
//     that brand has live, which is the number the index prints
//
// Reads the same warm caches /brand uses, so this costs nothing after warmup.
export async function fetchBrandList() {
  try {
    const [items, bgs, hidden] = await Promise.all([
      fetchItemsRetrying(),
      fetchBrandBackgrounds(),
      fetchShowcaseHiddenBrands(),
    ]);
    const byBrand = new Map();
    for (const item of items) {
      if (!item.brand_id) continue;
      const brand = (item.brand_name || "").toLowerCase().trim();
      if (!brand || hidden.has(brand)) continue;
      let entry = byBrand.get(brand);
      if (!entry) {
        entry = { count: 0, shots: [] };
        byBrand.set(brand, entry);
      }
      entry.count += 1;
      if (entry.shots.length < SHOTS_PER_BRAND) {
        const cover = coverOf(item);
        if (cover) entry.shots.push(cover);
      }
    }
    const list = [...byBrand.keys()]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => {
        const bg = bgs[name];
        const { count, shots } = byBrand.get(name);
        return {
          name,
          url: bg && bg.type !== "video" ? bg.url : null,
          count,
          shots,
        };
      });
    if (list.length) _lastList = list;
    return list;
  } catch {
    return [];
  }
}
